import React, { useState, useMemo, useCallback } from 'react';
import {
  List,
  Datagrid,
  TextField,
  ReferenceField,
  EditButton,
  ShowButton,
  TopToolbar,
  FunctionField,
  SearchInput,
} from 'react-admin';
import { Card, Typography, Box } from '@mui/material';
import {
  Add as AddIcon,
  TableChart as TableIcon,
  LocationOn as ZoneIcon,
  Factory as PlantIcon,
} from '@mui/icons-material';
import HierarchicalFilter, {
  HierarchicalFilterSelections,
} from '../../components/HierarchicalFilter';
import DeleteWithPasswordButton from '@/components/DeleteWithPasswordButton';
import CustomCreateButton from '@/components/CustomCreateButton';

const TableListActions = () => (
  <TopToolbar>
    <CustomCreateButton
      variant='contained'
      label='Crear Tabla'
      icon={<AddIcon />}
      sx={{
        height: '40px',
        backgroundColor: 'primary.main',
        '&:hover': {
          backgroundColor: 'primary.dark',
        },
      }}
    />
  </TopToolbar>
);

// Filtros para la búsqueda
const tableFilters = [
  <SearchInput
    key='search'
    source='q'
    placeholder='Buscar tablas...'
    alwaysOn
    size='small'
    sx={{ 
      minWidth: 200,
      mb: 0.5
    }}
  />,
];

export const TablesList = () => {
  const [filterValues, setFilterValues] = useState<HierarchicalFilterSelections>({});
  const [pagination, setPagination] = useState({ page: 1, perPage: 5 }); // ✅ AGREGAR PAGINACIÓN

  // ✅ USAR useCallback COMO EN DevicesList
  const handleFilterChange = useCallback((selections: HierarchicalFilterSelections) => {
    setFilterValues(selections);
    setPagination(prev => ({ ...prev, page: 1 })); // ✅ RESET PAGINACIÓN
  }, []);

  // ✅ AGREGAR PAGINACIÓN SERVIDOR COMO EN DevicesList
  const filter = useMemo(() => {
    const filterObj: any = {};

    if (filterValues.plantId) {
      filterObj.plantId = filterValues.plantId;
    }

    if (filterValues.zoneId) {
      filterObj.zoneId = filterValues.zoneId;
    }

    // ✅ PAGINACIÓN SERVIDOR - IGUAL QUE DevicesList
    filterObj.isPaginated = true;
    filterObj.page = pagination.page;
    filterObj.pageSize = pagination.perPage;

    console.log('🔍 Tables filter built:', filterObj);
    return filterObj;
  }, [filterValues.plantId, filterValues.zoneId, pagination.page, pagination.perPage]); // ✅ AGREGAR PAGINACIÓN A DEPENDENCIAS

  return (
    <Card elevation={1} sx={{ m: 2, p: 2 }}>
      <Box
        sx={{ p: 3, pt: 2, pb: 2.5, borderBottom: 1, borderColor: 'divider' }}
      >
        <Typography
          variant='h5'
          component='h1'
          sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
        >
          <TableIcon color='primary' />
          Gestión de Tablas
        </Typography>
        <Typography variant='body2' color='text.secondary' sx={{ mt: 1 }}>
          Gestiona las tablas del sistema organizadas por zonas
        </Typography>
      </Box>

      {/* Filtro Jerárquico */}
      <Box sx={{ p: 3, borderBottom: 1, borderColor: 'divider' }}>
        <Typography variant='h6' gutterBottom color='primary'>
          Filtros de Búsqueda
        </Typography>
        <HierarchicalFilter
          levels={['plant', 'zone']}
          onChange={handleFilterChange}
          size='medium'
        />

        {(filterValues.plantId || filterValues.zoneId) && (
          <Box sx={{ mt: 2 }}>
            <Typography variant='body2' color='text.secondary'>
              Filtros activos:
              {filterValues.plantName && ` Planta: ${filterValues.plantName}`}
              {filterValues.zoneName && ` → Zona: ${filterValues.zoneName}`}
            </Typography>
          </Box>
        )}

        {/* ✅ AGREGAR INDICADOR CUANDO NO HAY FILTROS */}
        {!filterValues.plantId && !filterValues.zoneId && (
          <Box sx={{ mt: 2 }}>
            <Typography variant='body2' color='info.main'>
              ✓ Mostrando todas las tablas del sistema
            </Typography>
          </Box>
        )}
      </Box>

      {/* ✅ AGREGAR perPage DINÁMICO COMO EN DevicesList */}
      <List
        title={false}
        actions={<TableListActions />}
        filter={filter}
        filters={tableFilters}
        perPage={pagination.perPage} // ✅ USAR perPage DEL STATE
        sort={{ field: 'tableName', order: 'ASC' }}
        exporter={false}
        sx={{ 
          '& .RaList-main': { mt: 0, p: 0 },
          '& .RaDatagrid-rowCell, & .RaDatagrid-headerCell': {
            padding: '6px 8px',
          }
        }}
      >
        <Box sx={{
          width: '100%',
          overflowX: 'auto',
          pb: 1,
        }}>
          <Datagrid
            rowClick='show'
            sx={{ 
              '& .RaDatagrid-headerCell': { fontWeight: 'bold' },
              tableLayout: 'fixed',
              width: '100%',
            }}
          >
            <TextField source='id' label='ID' sx={{ minWidth: 50, maxWidth: 50 }} />
            <TextField source='tableName' label='Nombre de la Tabla' sx={{ maxWidth: 200 }} />
            
            <ReferenceField
              source='zoneId'
              reference='zones'
              label='Zona'
              link='show'
              sx={{ maxWidth: 150 }}
            >
              <FunctionField
                render={(record: any) => {
                  if (!record) return 'Sin zona';
                  return (
                    <Box display='flex' alignItems='center' gap={1}>
                      <ZoneIcon color='action' fontSize='small' />
                      <span>
                        {record.zoneName || record.name || 'Zona sin nombre'}
                      </span>
                    </Box>
                  );
                }}
              />
            </ReferenceField>
            
            <FunctionField
              label='Planta'
              render={(record: any) => {
                if (!record || !record.zoneId) return 'Sin planta';
                return (
                  <ReferenceField
                    record={{ id: record.zoneId }}
                    source='id'
                    reference='zones'
                    link={false}
                  >
                    <ReferenceField
                      source='plantId'
                      reference='plants'
                      link='show'
                    >
                      <FunctionField
                        render={(plantRecord: any) => {
                          if (!plantRecord) return 'Sin planta';
                          return (
                            <Box display='flex' alignItems='center' gap={1}>
                              <PlantIcon color='action' fontSize='small' />
                              <span>
                                {plantRecord.plantName ||
                                  plantRecord.name ||
                                  'Planta sin nombre'}
                              </span>
                            </Box>
                          );
                        }}
                      />
                    </ReferenceField>
                  </ReferenceField>
                );
              }}
              sortable={false}
              sx={{ maxWidth: 150 }}
            />
            
            <ShowButton label='' />
            <EditButton label='' />
            <DeleteWithPasswordButton resource='tables' label='' />
          </Datagrid>
        </Box>
      </List>
    </Card>
  );
};

export default TablesList;